import WebsiteTemplatesHero from "@/components/Home/v2/WebsiteTemplatesHeroAnimation";
import CtaBannerV2 from "@/components/Home/v2/CtaBannerV2";
import FooterV2 from "@/components/Home/v2/FooterV2";
import NavbarV2 from "@/components/Home/v2/NavbarV2";
import Text from "@/components/Home/v2/shared/Text";
import GraphyHead from "@/components/organisms/GraphyHead";
import { homeV2Data } from "@/shared/data/home.v2.data";
import { faArrowUpRight } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Image from "next/image";
import Link from "next/link";
import { getCDNURL } from "@/shared/utils/commons";

const BASE = `${getCDNURL()}graphy-web/V4/images/Website Templates/Templates image`;

type Template = {
  name: string;
  image: string;
  previewHref?: string;
};

const TEMPLATES: Template[] = [
  {
    name: "Culinary Website",
    image: `${BASE}/Culinery Website.png`,
    previewHref: "https://savoryplates.graphy.com/",
  },
  {
    name: "Education Website",
    image: `${BASE}/Education Website.png`,
    previewHref: "https://sharis.graphy.com/",
  },
  {
    name: "Training Website",
    image: `${BASE}/Training website.png`,
    previewHref: "https://endurance.graphy.com/",
  },
  {
    name: "Photography Website",
    image: `${BASE}/Photography website.png`,
    previewHref: "https://form.graphy.com/",
  },
  {
    name: "Interior Design Website",
    image: `${BASE}/Interior design website.png`,
    previewHref: "https://sixtinedesign.graphy.com/",
  },
  {
    name: "Crypto Trading Website",
    image: `${BASE}/Cryto trading website.png`,
    previewHref: "https://aetheris.graphy.com/",
  },
  {
    name: "VR Development Website",
    image: `${BASE}/VR Development website.png`,
    previewHref: "https://flux.graphy.com/",
  },
  {
    name: "Skill Training Website",
    image: `${BASE}/Skill training website.png`,
    previewHref: "https://spectra.graphy.com/",
  },
  {
    name: "Fashion Academy Website",
    image: `${BASE}/Fashion Academy Website.png`,
    previewHref: "https://modelportfolios.graphy.com/",
  },
];

const TemplateCard = ({ template }: { template: Template }) => {
  const CardWrapper = template.previewHref
    ? ({ children }: { children: React.ReactNode }) => (
        <Link
          href={template.previewHref!}
          target="_blank"
          rel="noopener noreferrer"
          className="block overflow-hidden rounded-2xl border border-slateStroke bg-white shadow-sm transition-all duration-200 hover:-translate-y-1 hover:shadow-lg"
        >
          {children}
        </Link>
      )
    : ({ children }: { children: React.ReactNode }) => (
        <div className="overflow-hidden rounded-2xl border border-slateStroke bg-white shadow-sm">
          {children}
        </div>
      );

  return (
  <CardWrapper>
    <div className="relative h-[200px] w-full overflow-hidden">
      <Image
        src={template.image}
        alt={template.name}
        fill
        className="object-cover"
      />
    </div>
    <div className="flex items-center justify-between px-5 py-4">
      <Text as="span" variant="body-lg" className="font-normal text-neutralBlack">
        {template.name}
      </Text>
      <span className="flex items-center gap-1 text-[14px] font-medium text-[#5766F2]">
        Preview{" "}
        <FontAwesomeIcon icon={faArrowUpRight} className="h-3 w-3" />
      </span>
    </div>
  </CardWrapper>
  );
};

const WebsiteTemplatesPage = () => {
  return (
    <div className="home-v2-scope min-h-screen bg-white">
      <GraphyHead
        title="Website Templates — Graphy"
        description="Explore high-converting website templates built with AI. Choose a ready-made template, customize it your way, and launch without writing a single line of code."
        canonicalPath="/website-templates"
        countryCode=""
      />

      <NavbarV2 />

      <WebsiteTemplatesHero />

      {/* ── Browse Templates ── */}
      <section className="w-full bg-white pb-20 pt-10 md:pb-28 md:pt-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl text-center">
            <Text as="h2" variant="display-lg" className="text-neutralBlack">
              Browse our Templates
            </Text>
            <Text as="p" variant="body-lg" className="mt-3 text-darkSlate04">
              Explore professionally designed templates and choose the perfect
              starting point for your website.
            </Text>
          </div>

          <div className="mx-auto mt-12 grid max-w-6xl grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {TEMPLATES.map((template) => (
              <TemplateCard key={template.name} template={template} />
            ))}
          </div>
        </div>
      </section>

      <CtaBannerV2 {...homeV2Data.ctaBanner} />
      <FooterV2 {...homeV2Data.footer} />
    </div>
  );
};

export default WebsiteTemplatesPage;
